import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Switch
} from '@mui/material';
import { useQuery } from '@apollo/client';

import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import ArgonTypography from 'components/ArgonTypography';
import DialogWrapper from 'components/DialogWrapper';
import CollapsibleCardWrapper from 'components/CollapsibleCardWrapper';
import FormikTextInput from 'components/FormikTextInput';
import FormikYesNoInput from 'components/FormikYesNoInput';
import ArgonSelect from 'components/ArgonSelect';

import { EVENT_GET_BY_ID } from 'GraphQL/Events/queries';
import { EVENTINSTANCES_GET_BY_EVENTID } from 'GraphQL/EventInstances/queries';
import selectData from 'options/selectData';
import { cloneEvent } from 'services/REST_API/events';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { getSession } from 'services/cognito/cognitoAccount';

const validationSchema = Yup.object({
  clonedEventName: Yup.string(),
  cloneTeamRoles: Yup.boolean(),
  eventInstancesToBeCloned: Yup.array().of(
    Yup.object({
      eventInstanceId: Yup.string().required(),
      selected: Yup.boolean(),
      cloneAllDivisions: Yup.boolean(),
      divisionsToBeCloned: Yup.array().when('cloneAllDivisions', {
        is: false,
        then: Yup.array().min(1, 'At least one division must be selected'),
        otherwise: Yup.array()
      }),
      cloneWorkouts: Yup.boolean(),
      cloneSpectatorTickets: Yup.boolean(),
      cloneVolunteerPositions: Yup.boolean()
    })
  )
});

function CloneEventDialog({ open, onClose, eventId, onSubmit }) {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();

  const [loading, setLoading] = useState(false);
  const [initialValuesReady, setInitialValuesReady] = useState(false);

  // Fetch event data
  const {
    data: eventData,
    loading: eventLoading,
    error: eventError
  } = useQuery(EVENT_GET_BY_ID, {
    variables: { eventId },
    skip: !open || !eventId
  });

  // Fetch event instances data
  const {
    data: instancesData,
    loading: instancesLoading,
    error: instancesError
  } = useQuery(EVENTINSTANCES_GET_BY_EVENTID, {
    variables: { eventId },
    skip: !open || !eventId
  });

  // Initialize formik with empty values
  const formik = useFormik({
    initialValues: {
      eventId: '',
      clonedEventName: '',
      cloneTeamRoles: true,
      eventInstancesToBeCloned: []
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
    enableReinitialize: true
  });

  // Update formik values when data is loaded
  useEffect(() => {
    if (eventData?.event && instancesData?.eventInstances) {
      formik.setValues({
        eventId,
        clonedEventName: `${eventData.event.name} (cloned)`,
        cloneTeamRoles: true,
        eventInstancesToBeCloned: [...instancesData.eventInstances]
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
          .map((instance) => ({
            eventInstanceId: instance._id,
            eventInstanceName:
              instance.name ||
              selectData?.eventInstanceTypes?.find(
                (type) => type.value === instance.type
              )?.label ||
              'Unnamed Instance',
            divisions: instance.divisions || [],
            selected: false,
            cloneAllDivisions: true,
            divisionsToBeCloned: [],
            cloneWorkouts: true,
            cloneSpectatorTickets: true,
            cloneVolunteerPositions: true
          }))
      });
      setInitialValuesReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData, instancesData, eventId]);

  async function handleSubmit(values) {
    setLoading(true);
    try {
      const selectedInstances = values.eventInstancesToBeCloned
        .filter((instance) => instance.selected)
        .map((instance) => ({
          eventInstanceId: instance.eventInstanceId,
          cloneAllDivisions: instance.cloneAllDivisions,
          divisionsToBeCloned: instance.cloneAllDivisions
            ? []
            : instance.divisionsToBeCloned,
          cloneWorkouts: instance.cloneWorkouts,
          cloneSpectatorTickets: instance.cloneSpectatorTickets,
          cloneVolunteerPositions: instance.cloneVolunteerPositions
        }));

      const payload = {
        eventId: values.eventId,
        clonedEventName: values.clonedEventName,
        cloneTeamRoles: values.cloneTeamRoles,
        eventInstancesToBeCloned: selectedInstances
      };

      console.log('clone event payload', payload);

      // onSubmit && onSubmit(payload);

      const response = await cloneEvent(payload);

      console.log('clone event response', response);

      if (response?.clonedEvent?._id) {
        await refreshSession();
        const session = await getSession();
        console.log('clone events: session after refresh', session);

        navigate(`/organizer/events/${response?.clonedEvent?._id}`);
      }

      onClose();
    } catch (error) {
      console.error('Error cloning event:', error);
    } finally {
      setLoading(false);
    }
  }

  const isDataLoading = eventLoading || instancesLoading;
  const hasError = eventError || instancesError;

  // Show loading state when fetching data
  if (isDataLoading && open) {
    return (
      <DialogWrapper
        open={open}
        onClose={onClose}
        title="Clone Event"
        fullWidth
        maxWidth="md"
      >
        <ArgonBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={10}
        >
          <CircularProgress />
        </ArgonBox>
      </DialogWrapper>
    );
  }

  // Show error state
  if (hasError && open) {
    return (
      <DialogWrapper
        open={open}
        onClose={onClose}
        title="Clone Event"
        fullWidth
        maxWidth="md"
      >
        <ArgonBox textAlign="center" py={5}>
          <ArgonTypography variant="h6" color="error">
            Error loading event data
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" mt={2}>
            {eventError?.message || instancesError?.message}
          </ArgonTypography>
          <ArgonButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            mt={3}
          >
            Close
          </ArgonButton>
        </ArgonBox>
      </DialogWrapper>
    );
  }

  // Don't render form until initial values are ready
  if (!initialValuesReady) return null;

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Clone Event"
      fullWidth
      maxWidth="md"
    >
      <div style={{ overflowY: 'auto', maxHeight: '80vh' }}>
        <form onSubmit={formik.handleSubmit}>
          <ArgonBox mb={3}>
            <FormikTextInput
              id="clonedEventName"
              name="clonedEventName"
              label="New Event Name"
              placeholder="Enter new event name (optional)"
              formik={formik}
            />
          </ArgonBox>

          <ArgonBox mb={3} ml={0.5}>
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <ArgonBox display="flex" flexDirection="column" lineHeight={1.4}>
                <ArgonTypography variant="body2" fontWeight="medium">
                  Clone Team Role Assignments
                </ArgonTypography>
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="regular"
                >
                  Clone team role assignments for all event instances.
                </ArgonTypography>
              </ArgonBox>
              <Switch
                checked={formik.values.cloneTeamRoles}
                onChange={(e) => {
                  formik.setFieldValue('cloneTeamRoles', e.target.checked);
                }}
                color="primary"
              />
            </ArgonBox>
          </ArgonBox>

          <ArgonBox mb={3} ml={0.5}>
            <ArgonBox
              display="flex"
              flexDirection="column"
              lineHeight={1.4}
              mb={2}
            >
              <ArgonTypography variant="body2" fontWeight="medium">
                Event Instances to Clone
              </ArgonTypography>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="regular"
              >
                Select which event instances to clone and select which
                information to be cloned.
              </ArgonTypography>
            </ArgonBox>

            {formik.values.eventInstancesToBeCloned.map((instance, index) => (
              <ArgonBox key={instance.eventInstanceId} mb={2}>
                <CollapsibleCardWrapper
                  title={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={instance.selected}
                            onChange={(e) => {
                              formik.setFieldValue(
                                `eventInstancesToBeCloned[${index}].selected`,
                                e.target.checked
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label=""
                        sx={{ mr: 1 }}
                      />
                      {instance.eventInstanceName}
                    </ArgonBox>
                  }
                  isOpen={instance.selected}
                  showChevron={instance.selected}
                >
                  {instance.selected && (
                    <ArgonBox px={3}>
                      <ArgonBox mb={2}>
                        <FormikYesNoInput
                          id={`cloneAllDivisions-${index}`}
                          name={`eventInstancesToBeCloned[${index}].cloneAllDivisions`}
                          label="Clone All Divisions"
                          yesInfo=""
                          noInfo="only selected divisions"
                          formik={formik}
                        />
                      </ArgonBox>

                      {!instance.cloneAllDivisions &&
                        instance.divisions.length > 0 && (
                          <ArgonBox mb={2}>
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              mb={1}
                            >
                              Select Divisions to Clone *
                            </ArgonTypography>
                            <ArgonSelect
                              isMulti
                              options={instance.divisions.map((div) => ({
                                value: div._id,
                                label: div.name
                              }))}
                              value={instance.divisionsToBeCloned.map(
                                (divId) => ({
                                  value: divId,
                                  label:
                                    instance.divisions.find(
                                      (d) => d._id === divId
                                    )?.name || divId
                                })
                              )}
                              onChange={(selectedOptions) => {
                                formik.setFieldValue(
                                  `eventInstancesToBeCloned[${index}].divisionsToBeCloned`,
                                  selectedOptions
                                    ? selectedOptions.map((opt) => opt.value)
                                    : []
                                );
                              }}
                              placeholder="Select divisions..."
                            />
                            {formik.errors.eventInstancesToBeCloned?.[index]
                              ?.divisionsToBeCloned && (
                              <ArgonTypography
                                variant="caption"
                                color="error"
                                mt={1}
                              >
                                {
                                  formik.errors.eventInstancesToBeCloned[index]
                                    .divisionsToBeCloned
                                }
                              </ArgonTypography>
                            )}
                          </ArgonBox>
                        )}

                      <ArgonBox mb={2}>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <ArgonTypography
                            display="block"
                            variant="caption"
                            fontWeight="bold"
                            ml={0.5}
                          >
                            Clone Workouts
                          </ArgonTypography>
                          <Switch
                            checked={instance.cloneWorkouts}
                            onChange={(e) => {
                              formik.setFieldValue(
                                `eventInstancesToBeCloned[${index}].cloneWorkouts`,
                                e.target.checked
                              );
                            }}
                            color="primary"
                          />
                        </ArgonBox>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <ArgonTypography
                            display="block"
                            variant="caption"
                            fontWeight="bold"
                            ml={0.5}
                          >
                            Clone Spectator Tickets
                          </ArgonTypography>
                          <Switch
                            checked={instance.cloneSpectatorTickets}
                            onChange={(e) => {
                              formik.setFieldValue(
                                `eventInstancesToBeCloned[${index}].cloneSpectatorTickets`,
                                e.target.checked
                              );
                            }}
                            color="primary"
                          />
                        </ArgonBox>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          ml={0.5}
                        >
                          <ArgonTypography
                            display="block"
                            variant="caption"
                            fontWeight="bold"
                          >
                            Clone Volunteer Positions
                          </ArgonTypography>
                          <Switch
                            checked={instance.cloneVolunteerPositions}
                            onChange={(e) => {
                              formik.setFieldValue(
                                `eventInstancesToBeCloned[${index}].cloneVolunteerPositions`,
                                e.target.checked
                              );
                            }}
                            color="primary"
                          />
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  )}
                </CollapsibleCardWrapper>
              </ArgonBox>
            ))}
          </ArgonBox>
        </form>
      </div>
      <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
        <ArgonButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </ArgonButton>
        <ArgonButton
          onClick={() => formik.handleSubmit()}
          variant="gradient"
          color="info"
          loading={loading}
          disabled={
            loading ||
            !formik.values.eventInstancesToBeCloned.some((i) => i.selected)
          }
        >
          {/* {loading ? (
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} color="inherit" />
              Cloning...
            </ArgonBox>
          ) : (
            'Clone Event'
          )} */}
          Clone Event
        </ArgonButton>
      </ArgonBox>
    </DialogWrapper>
  );
}

CloneEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CloneEventDialog;
